import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const AdminFinancialReports = () => {
  const [dashboardData, setDashboardData] = useState({
    total_revenue: 0,
    total_commission: 0,
    total_organizer_earnings: 0,
    pending_payout_requests: 0
  });
  const [earningsByOrganizer, setEarningsByOrganizer] = useState([]);
  const [earningsByEvent, setEarningsByEvent] = useState([]);
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(true);
  const [isLoadingOrganizers, setIsLoadingOrganizers] = useState(true);
  const [isLoadingEvents, setIsLoadingEvents] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [commissionModal, setCommissionModal] = useState({ show: false, organizer: null, rate: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadDashboard();
    loadEarningsByOrganizer();
    loadEarningsByEvent();
  }, []);

  const loadDashboard = async () => {
    try {
      setIsLoadingDashboard(true);
      const response = await api.get('/admin/financial/dashboard');
      if (response.data.status === 'success') {
        setDashboardData(response.data.data);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setIsLoadingDashboard(false);
    }
  };

  const loadEarningsByOrganizer = async () => {
    try {
      setIsLoadingOrganizers(true);
      const response = await api.get('/admin/financial/earnings-by-organizer');
      if (response.data.status === 'success') {
        setEarningsByOrganizer(response.data.data);
      }
    } catch (error) {
      console.error('Error loading organizer earnings:', error);
    } finally {
      setIsLoadingOrganizers(false);
    }
  };

  const loadEarningsByEvent = async () => {
    try {
      setIsLoadingEvents(true);
      const response = await api.get('/admin/financial/earnings-by-event');
      if (response.data.status === 'success') {
        setEarningsByEvent(response.data.data);
      }
    } catch (error) {
      console.error('Error loading event earnings:', error);
    } finally {
      setIsLoadingEvents(false);
    }
  };

  const handleUpdateCommission = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const response = await api.put(`/admin/financial/organizers/${commissionModal.organizer.organizer_id}/commission`, {
        commission_rate: parseFloat(commissionModal.rate)
      });

      if (response.data.status === 'success') {
        setSuccess(`Commission rate updated successfully for ${commissionModal.organizer.organizer_name}`);
        setCommissionModal({ show: false, organizer: null, rate: '' });
        loadEarningsByOrganizer();
        loadDashboard();
      }
    } catch (error) {
      if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError('Failed to update commission rate');
      }
    }
  };

  const openCommissionModal = (organizer) => {
    setCommissionModal({
      show: true,
      organizer: organizer,
      rate: organizer.commission_rate || '10'
    });
    setError('');
    setSuccess('');
  };

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Financial Management</h2>
      </div>

      {/* Success/Error Messages */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="close" onClick={() => setError('')}>
            <span>&times;</span>
          </button>
        </div>
      )}

      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {success}
          <button type="button" className="close" onClick={() => setSuccess('')}>
            <span>&times;</span>
          </button>
        </div>
      )}

      {/* Navigation Tabs */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            Dashboard
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'organizers' ? 'active' : ''}`}
            onClick={() => setActiveTab('organizers')}
          >
            By Organizer
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'events' ? 'active' : ''}`}
            onClick={() => setActiveTab('events')}
          >
            By Event
          </button>
        </li>
      </ul>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div className="row">
          {isLoadingDashboard ? (
            <div className="col-12 text-center">
              <div className="spinner-border" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              <div className="col-md-3 mb-4">
                <div className="card border-primary">
                  <div className="card-body">
                    <h5 className="card-title text-muted">Total Revenue</h5>
                    <h2 className="mb-0 text-primary">${parseFloat(dashboardData.total_revenue).toFixed(2)}</h2>
                  </div>
                </div>
              </div>
              <div className="col-md-3 mb-4">
                <div className="card border-success">
                  <div className="card-body">
                    <h5 className="card-title text-muted">Total Commission</h5>
                    <h2 className="mb-0 text-success">${parseFloat(dashboardData.total_commission).toFixed(2)}</h2>
                    <small className="text-muted">Platform earnings</small>
                  </div>
                </div>
              </div>
              <div className="col-md-3 mb-4">
                <div className="card">
                  <div className="card-body">
                    <h5 className="card-title text-muted">Organizer Earnings</h5>
                    <h2 className="mb-0">${parseFloat(dashboardData.total_organizer_earnings).toFixed(2)}</h2>
                  </div>
                </div>
              </div>
              <div className="col-md-3 mb-4">
                <div className="card border-warning">
                  <div className="card-body">
                    <h5 className="card-title text-muted">Pending Payouts</h5>
                    <h2 className="mb-0 text-warning">${parseFloat(dashboardData.pending_payout_requests).toFixed(2)}</h2>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Earnings by Organizer Tab */}
      {activeTab === 'organizers' && (
        <div className="card">
          <div className="card-body">
            <h5 className="card-title">Earnings by Organizer</h5>
            {isLoadingOrganizers ? (
              <div className="text-center py-5">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            ) : earningsByOrganizer.length === 0 ? (
              <p className="text-center text-muted py-5">No earnings data available</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Organizer</th>
                      <th>Email</th>
                      <th>Total Revenue</th>
                      <th>Commission Rate</th>
                      <th>Total Commission</th>
                      <th>Organizer Earnings</th>
                      <th>Transactions</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {earningsByOrganizer.map((organizer) => (
                      <tr key={organizer.organizer_id}>
                        <td>{organizer.organizer_name}</td>
                        <td>{organizer.organizer_email}</td>
                        <td className="font-weight-bold">${parseFloat(organizer.total_revenue).toFixed(2)}</td>
                        <td>
                          <span className="badge badge-info">{parseFloat(organizer.commission_rate).toFixed(1)}%</span>
                        </td>
                        <td className="text-success">${parseFloat(organizer.total_commission).toFixed(2)}</td>
                        <td className="font-weight-bold">${parseFloat(organizer.organizer_earnings).toFixed(2)}</td>
                        <td>{organizer.transaction_count}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => openCommissionModal(organizer)}
                          >
                            <svg className="w-4 h-4" style={{ width: '1rem', height: '1rem', display: 'inline' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            {' '}Edit Commission
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="font-weight-bold">
                      <td colSpan="2">Total</td>
                      <td>${earningsByOrganizer.reduce((sum, o) => sum + parseFloat(o.total_revenue), 0).toFixed(2)}</td>
                      <td></td>
                      <td className="text-success">${earningsByOrganizer.reduce((sum, o) => sum + parseFloat(o.total_commission), 0).toFixed(2)}</td>
                      <td>${earningsByOrganizer.reduce((sum, o) => sum + parseFloat(o.organizer_earnings), 0).toFixed(2)}</td>
                      <td>{earningsByOrganizer.reduce((sum, o) => sum + parseInt(o.transaction_count), 0)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Earnings by Event Tab */}
      {activeTab === 'events' && (
        <div className="card">
          <div className="card-body">
            <h5 className="card-title">Earnings by Event</h5>
            {isLoadingEvents ? (
              <div className="text-center py-5">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            ) : earningsByEvent.length === 0 ? (
              <p className="text-center text-muted py-5">No earnings data available</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th>Organizer</th>
                      <th>Category</th>
                      <th>Total Revenue</th>
                      <th>Commission Rate</th>
                      <th>Commission</th>
                      <th>Organizer Earnings</th>
                      <th>Transactions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {earningsByEvent.map((event) => (
                      <tr key={event.event_id}>
                        <td>{event.event_title}</td>
                        <td>{event.organizer_name}</td>
                        <td>{event.event_category}</td>
                        <td className="font-weight-bold">${parseFloat(event.total_revenue).toFixed(2)}</td>
                        <td>
                          <span className="badge badge-info">{parseFloat(event.commission_rate).toFixed(1)}%</span>
                        </td>
                        <td className="text-success">${parseFloat(event.total_commission).toFixed(2)}</td>
                        <td className="font-weight-bold">${parseFloat(event.organizer_earnings).toFixed(2)}</td>
                        <td>{event.transaction_count}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="font-weight-bold">
                      <td colSpan="3">Total</td>
                      <td>${earningsByEvent.reduce((sum, e) => sum + parseFloat(e.total_revenue), 0).toFixed(2)}</td>
                      <td></td>
                      <td className="text-success">${earningsByEvent.reduce((sum, e) => sum + parseFloat(e.total_commission), 0).toFixed(2)}</td>
                      <td>${earningsByEvent.reduce((sum, e) => sum + parseFloat(e.organizer_earnings), 0).toFixed(2)}</td>
                      <td>{earningsByEvent.reduce((sum, e) => sum + parseInt(e.transaction_count), 0)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Commission Modal */}
      {commissionModal.show && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Update Commission Rate</h5>
                <button type="button" className="close" onClick={() => setCommissionModal({ show: false, organizer: null, rate: '' })}>
                  <span>&times;</span>
                </button>
              </div>
              <form onSubmit={handleUpdateCommission}>
                <div className="modal-body">
                  <p>
                    <strong>Organizer:</strong> {commissionModal.organizer?.organizer_name}
                  </p>
                  <p className="text-muted small">
                    Current commission rate: {parseFloat(commissionModal.organizer?.commission_rate).toFixed(1)}%
                  </p>
                  <div className="form-group">
                    <label htmlFor="commissionRate">New Commission Rate (%)</label>
                    <input
                      type="number"
                      className="form-control"
                      id="commissionRate"
                      value={commissionModal.rate}
                      onChange={(e) => setCommissionModal({ ...commissionModal, rate: e.target.value })}
                      step="0.1"
                      min="0"
                      max="100"
                      required
                    />
                    <small className="form-text text-muted">
                      Platform commission percentage (0-100%)
                    </small>
                  </div>
                  <div className="alert alert-info">
                    <small>
                      <strong>Note:</strong> This will apply to all future transactions for this organizer. 
                      Existing transactions will keep their original commission rate.
                    </small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setCommissionModal({ show: false, organizer: null, rate: '' })}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Update Commission
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFinancialReports;
